// §2A step 8 — Handoff. Parent confirms iPad goes to the kid before the
// diagnostic. Without this step, the parent often does the diagnostic and
// corrupts placement.

import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useAccount, useKids } from "@/lib/account";
import { Pal } from "@/components/Pal";
import { useActivePal } from "@/lib/pal";
import { useAppState } from "@/App/AppStateContext.tsx";
import { useOnboardingDraft } from "@/lib/onboardingDraft";
import { useFinishOnboarding } from "@/lib/useFinishOnboarding";
import { signInWithGoogle, recordConsent, recordSchoolAuthorityDirectConsent } from "@/supabaseClient.js";
import { isSignedMethod } from "@/App/consentGate.js";
import { getUserRole } from "@/lib/userRole";
import { TEACHER_ADD_KID_REDIRECT_COPY, useFamilyAddKidGateBlocked } from "@/lib/teacherAddKidGate";
import { childDisplayName } from "@/App/progress";

// MA-04 signed-form attestation statement (DRAFT — the exact legal wording is the
// §2 counsel hold; see docs/legal/PENDING-COPY.md). Shown + acknowledged before a
// signed consent row is written.
const SIGNED_STATEMENT =
  "I am the parent or legal guardian of this child, and I consent to PencilWise " +
  "collecting and using my child's first name, age, and practice activity to " +
  "personalize their learning, as described in the COPPA notice, under 16 CFR Part 312.";

// MA-464 school-authority-direct attestation (DRAFT — final legal wording is the
// §2 counsel hold; see docs/legal/PENDING-COPY.md). Shown to TEACHER-ROLE accounts
// instead of the parental-guardianship statement: a classroom kid's cloud write is
// authorized by the school (COPPA school-authorization path, MA-DEC-0802-WS8),
// never by a teacher pretending to be the parent. Typed name + date + explicit
// acknowledgement are ALWAYS required on this lane, regardless of VPC method.
const SCHOOL_DIRECT_STATEMENT =
  "I am this student's teacher or an authorized school official, and my school or " +
  "organization has authorized PencilWise to collect and use this student's first " +
  "name, age, and practice activity for educational purposes in my classroom, under " +
  "COPPA's school-authorization provision.";

const PAL_BG: Record<string, string> = {
  yellow: "bg-pal-yellow/30",
  blue: "bg-pal-blue/30",
  purple: "bg-pal-purple/30",
  orange: "bg-pal-orange/30",
  green: "bg-pal-green/30",
  pink: "bg-pal-pink/30",
};

// H-A4 shared step styles (H-0 tokens) — see onboarding.kid.name.tsx.
const EYEBROW = "text-xs font-bold uppercase tracking-[0.18em] text-terracotta-deep";
const CTA =
  "flex min-h-[52px] w-full scroll-mb-28 items-center justify-center rounded-2xl bg-primary px-6 text-base font-extrabold text-primary-foreground shadow-press transition-all active:translate-y-0.5 active:bg-primary-pressed active:shadow-none disabled:opacity-50";

export const Route = createFileRoute("/onboarding/kid/handoff")({
  head: () => ({ meta: [{ title: "PencilWise · Pass to your kid" }] }),
  component: Handoff,
});

function Handoff() {
  const navigate = useNavigate();
  const { data: account } = useAccount();
  const { data: kids = [] } = useKids();
  const kid = kids[kids.length - 1] ?? null;
  // Phase G3 Stage 4: read the new flow's draft + bridge to
  // pencilwise's gate-ladder data path.
  const { draft } = useOnboardingDraft();
  const app = useAppState();
  const { finish } = useFinishOnboarding();
  // useActivePal here pulls from AppStateContext, not from kids[]
  // (the storybook flow assumes the kid row is already persisted;
  // pencilwise commits at handoff via handleOnboardingComplete).
  const { color: palColor, name: palName } = useActivePal({
    mascotId: draft.mascotId,
    kidId: app?.activeChildId ?? null,
    kidName: draft.name || childDisplayName(app?.progress) || null,
  });
  const [busy, setBusy] = useState(false);
  // MA-544 — the underlying-flow-entry half of the retire-the-workaround
  // gate (the entry-affordance half lives in SettingsKids/WhosPlayingScreen
  // — see teacherAddKidGate.ts's header). This is the ACTUAL commit
  // chokepoint: nothing before this step writes a kid anywhere (the draft
  // is local-only, see onboardingDraft.ts), so gating render + the
  // `handoff()` commit itself here is sufficient to make blocking real —
  // even a teacher who reached this route directly (deep link, browser
  // back into a stale multi-step flow) cannot get past it once they own a
  // class. Role-check-first inside the gate means a parent-role account's
  // `blocked` is `false` from the first render and this component is
  // otherwise byte-untouched for them.
  const blocked = useFamilyAddKidGateBlocked(account?.id ?? null);

  // MA-04 signed-form method: collect a typed legal name + date + an explicit
  // acknowledgement before a signed consent row is written. Under the legacy
  // (beta click-through) method these stay unused and the CTA is always enabled.
  const signed = isSignedMethod();
  // MA-464 — teacher-role accounts attest school authority with a typed
  // name/date/ack ALWAYS (env-independent; at least as strict as the parent
  // signed method, since an operator asserts authority over another family's
  // child). Parent-role behavior is byte-identical to before.
  const teacherLane = getUserRole() === "teacher";
  const needsSignature = signed || teacherLane;
  const [signedName, setSignedName] = useState("");
  const [signedDate, setSignedDate] = useState("");
  const [ack, setAck] = useState(false);
  const signatureComplete =
    !needsSignature || (signedName.trim().length > 0 && signedDate.trim().length > 0 && ack);

  const handoff = async () => {
    if (!account) return;
    // MA-544 defense-in-depth: the render below already swaps this whole
    // step out for the routing note when blocked, so this branch is not
    // reachable through the UI — guarded anyway, same rationale as
    // SettingsKids' onAddKid guard.
    if (blocked) return;
    if (needsSignature && !signatureComplete) return; // defense-in-depth; CTA is also disabled
    setBusy(true);
    // MA-04 — record verifiable-parental-consent BEFORE the child-PII cloud
    // write. AWAITED so the consent_records row is committed before
    // createCloudStudent's gate reads it. Non-blocking on failure: a failed or
    // INCOMPLETE consent write leaves the kid local-only (the gate refuses the
    // cloud write), never a hard error — the parent is re-prompted from
    // Kids (studentsNeedingConsent). For the signed method, an
    // incomplete signature makes recordConsent write NOTHING (kid local-only).
    // MA-464 — the role at creation decides the lane: teacher-role records the
    // school-authority-direct attestation (school_authority_direct_v1), parent-
    // role records the COPPA lane exactly as before. Either way the row is
    // AWAITED before the child-PII cloud write, and a failed/incomplete write
    // leaves the kid local-only (the gate refuses), never a hard error.
    if (teacherLane) {
      await recordSchoolAuthorityDirectConsent({
        name: signedName,
        date: signedDate,
        statement: SCHOOL_DIRECT_STATEMENT,
      }).catch(() => {});
    } else {
      await recordConsent(
        signed
          ? { signature: { name: signedName, date: signedDate, acknowledged: ack, statement: SIGNED_STATEMENT } }
          : {},
      ).catch(() => {});
    }
    // De-mock (PR #15): the storybook `accounts.onboarding_state` write
    // had no pencilwise table (account state is local app-state). The
    // real onboarding progression is driven by handleOnboardingComplete
    // below; the removed write was a pure no-op. (MA-31: the ["account"]
    // invalidation that also lived here was a no-op — account is a useMemo
    // derivation, not a query — so it is removed too.)
    // T1.0: commit the draft through the explicit bridge hook. finish()
    // is idempotent (reuses an existing kid by name+age — no duplicates on
    // re-entry) and delegates the actual cloud+local kid creation to
    // App.jsx's handleOnboardingComplete (cloud-create path untouched),
    // then re-asserts the active child. Continue INTO the new flow's
    // diagnostic (not "/" — which would bounce off the gate ladder).
    const committed = await finish();
    if (!committed) {
      // Commit refused (no authenticated session) — stay put with the draft
      // intact and route to sign-in; it rehydrates post-OAuth. (Unreachable
      // while the gate ladder blocks unauth /onboarding, but self-defending.)
      setBusy(false);
      await signInWithGoogle().catch(() => {});
      return;
    }
    navigate({ to: "/onboarding/diagnostic", search: (prev) => prev });
  };

  // MA-544 — a class-owning teacher never sees the handoff form at all:
  // honest routing copy instead, same wording SettingsKids/WhosPlayingScreen
  // show at their own entry points, so a teacher who reached this deep-linked
  // step directly is redirected exactly like every other gated surface.
  if (blocked) {
    return (
      <div className="relative min-h-screen bg-cream paper-grain">
        <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-10 text-center">
          <p
            className="rounded-2xl bg-white/60 px-4 py-3 text-sm leading-relaxed text-muted-foreground"
            data-testid="add-kid-blocked-note"
          >
            {TEACHER_ADD_KID_REDIRECT_COPY}
          </p>
          <Link to="/teacher" data-testid="teacher-add-kid-redirect" className={`mt-4 ${CTA}`}>
            Go to your class roster
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-cream paper-grain">
      <div className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-10 text-center">
        <header className="pointer-events-none animate-slide-up">
          <p className={EYEBROW}>Almost there</p>
          <h1 className="mt-2 font-serif text-3xl font-bold tracking-tight">
            Pass the iPad to {kid?.name ?? "your kid"}.
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            The next 5 questions are a quick check-in so we can pick the right
            starting point. {kid?.name ?? "They"} should answer — no helping!
          </p>
        </header>

        <div
          className={`mx-auto my-8 grid h-32 w-32 place-items-center rounded-full ${PAL_BG[palColor]}`}
        >
          <Pal color={palColor} mood="cheer" size={96} className="animate-pal-bob" />
        </div>
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-muted-foreground">
          Hi, I'm {palName}
        </p>

        {/* MA-04 — DRAFT COPPA consent affirmation (final legal copy is a
            separate human hold; see docs/legal/PENDING-COPY.md). Continuing is
            the parent's click-through attestation; it records a consent row
            before any of the child's info is saved to the cloud. */}
        <p
          className="mt-6 rounded-2xl bg-white/60 px-4 py-3 text-left text-xs leading-relaxed text-muted-foreground"
          data-testid={teacherLane ? "consent-copy-school" : "consent-copy-parent"}
        >
          {teacherLane ? (
            <>
              <span className="font-bold text-foreground">Teacher or school staff:</span>{" "}
              By continuing you confirm your school or organization has authorized
              PencilWise to collect {kid?.name ?? "this student"}&rsquo;s first name,
              age, and practice activity for educational use in your classroom.
            </>
          ) : (
            <>
              <span className="font-bold text-foreground">Parent or guardian:</span>{" "}
              By continuing you confirm you are {kid?.name ?? "this child"}&rsquo;s
              parent or legal guardian and consent to PencilWise collecting your
              child&rsquo;s first name, age, and practice activity to personalize
              their learning.
            </>
          )}{" "}
          See our{" "}
          <a href="/coppa-notice" className="underline" target="_blank" rel="noopener noreferrer">COPPA notice</a>,{" "}
          <a href="/privacy" className="underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a>, and{" "}
          <a href="/terms" className="underline" target="_blank" rel="noopener noreferrer">Terms</a>.
        </p>
        {/* MA-04 signed-form method — a recorded signed attestation (typed legal
            name + date + explicit 16 CFR Part 312 acknowledgement). The gate opens
            ONLY on a completed signed row; an incomplete form keeps the kid
            local-only. DRAFT copy — final wording is the §2 counsel hold. */}
        {needsSignature && (
          <div className="mt-4 space-y-3 rounded-2xl bg-white/60 px-4 py-4 text-left">
            <label className="block text-xs font-bold text-foreground">
              {teacherLane ? "Your full legal name" : "Parent/guardian full legal name"}
              <input
                type="text"
                value={signedName}
                onChange={(e) => setSignedName(e.target.value)}
                autoComplete="name"
                placeholder="Full legal name"
                className="mt-1 min-h-[44px] w-full rounded-xl border border-border bg-white px-3 text-sm"
                aria-label={teacherLane ? "Teacher or school official full legal name" : "Parent or guardian full legal name"}
              />
            </label>
            <label className="block text-xs font-bold text-foreground">
              Date
              <input
                type="date"
                value={signedDate}
                onChange={(e) => setSignedDate(e.target.value)}
                className="mt-1 min-h-[44px] w-full rounded-xl border border-border bg-white px-3 text-sm"
                aria-label="Date of signature"
              />
            </label>
            <label className="flex items-start gap-2 text-xs leading-relaxed text-muted-foreground">
              <input
                type="checkbox"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0"
                aria-label={
                  teacherLane
                    ? "I attest my school or organization authorized this collection for educational use"
                    : "I am the parent or legal guardian and I consent under 16 CFR Part 312"
                }
              />
              <span data-testid="consent-statement">{teacherLane ? SCHOOL_DIRECT_STATEMENT : SIGNED_STATEMENT}</span>
            </label>
          </div>
        )}
        <button
          onClick={handoff}
          disabled={busy || (needsSignature && !signatureComplete)}
          className={`mt-4 ${CTA}`}
        >
          {busy ? "Starting…" : `I consent — I've handed it to ${kid?.name ?? "them"} →`}
        </button>
        <p className="mt-3 text-xs text-muted-foreground">
          {teacherLane
            ? "This confirms your school's authorization and that the diagnostic is being done by the student."
            : "This confirms you consent and that the diagnostic is being done by your child."}
        </p>
      </div>
    </div>
  );
}
